/**
 * Detect design-change, drawing, and document numbers in free text and
 * offer explicit link suggestions at the caret, and round-trip selected pills.
 * The marker keeps the database id; the visible number is never used as the id.
 *
 * Suggestions start on a partial identifier: a document code once it has a
 * hyphen (`MDL-`, `ACC-001`), and a design-change or drawing once the
 * chapter-section is present (`53-16`, `53-16-10`). A five-digit tail is a
 * drawing. A shorter tail can still grow into either a design change or a drawing.
 */

import { matchesSuggestion } from "./databaseSearch.mjs";

const ATA_RE = /(?<![A-Za-z0-9])\d{2}-\d{2}(?:-\d{0,5})?(?![A-Za-z0-9])/g;
const DOCUMENT_RE = /(?<![A-Za-z0-9])[A-Za-z]{2,6}-\d{0,4}(?![A-Za-z0-9])/g;

function ataEntityTypes(number) {
	const tail = String(number).split("-")[2] || "";
	if (tail.length >= 5) return ["Drawings"];
	return ["DesignChanges", "Drawings"];
}

const ENTITY_PATTERNS = [
	{ re: ATA_RE, entityTypes: ataEntityTypes },
	{ re: DOCUMENT_RE, entityTypes: () => ["Documents"] },
];

const MARKER_RE = /\[\[([^|\[\]]+)\|([^|\[\]]+)\|([^|\[\]]+)\|([^\]]*)\]\]/g;

const TYPE_LABELS = {
	Drawings: "Drawing",
	DesignChanges: "Design change",
	Documents: "Document",
	JobCards: "Job card",
};

function numberKey(value) {
	return String(value ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function typeLabel(entityType) {
	return TYPE_LABELS[entityType] || entityType || "";
}

function formatMarker({ entityType, number, entityId, title }) {
	return `[[${entityType}|${number}|${entityId}|${encodeURIComponent(title || "")}]]`;
}

function detectEntityNumbers(text) {
	const source = String(text || "");
	const found = [];
	for (const pattern of ENTITY_PATTERNS) {
		pattern.re.lastIndex = 0;
		let match = pattern.re.exec(source);
		while (match) {
			const entityTypes = pattern.entityTypes(match[0]);
			const foundMatch = {
				entityTypes,
				number: match[0],
				start: match.index,
				end: match.index + match[0].length,
			};
			if (entityTypes.length === 1) foundMatch.entityType = entityTypes[0];
			found.push(foundMatch);
			match = pattern.re.exec(source);
		}
	}
	found.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
	const accepted = [];
	let cursor = 0;
	for (const match of found) {
		if (match.start < cursor) continue;
		accepted.push(match);
		cursor = match.end;
	}
	return accepted;
}

function entityNumberAtCaret(text, offset) {
	return detectEntityNumbers(text).find((match) => match.start < offset && offset <= match.end) || null;
}

function parseModel(value) {
	const source = String(value || "");
	const segments = [];
	let last = 0;
	MARKER_RE.lastIndex = 0;
	let match = MARKER_RE.exec(source);
	while (match) {
		if (match.index > last) {
			segments.push({ type: "text", text: source.slice(last, match.index) });
		}
		segments.push({
			type: "entity",
			entityType: match[1],
			number: match[2],
			entityId: match[3],
			title: decodeURIComponent(match[4] || ""),
		});
		last = match.index + match[0].length;
		match = MARKER_RE.exec(source);
	}
	if (last < source.length) {
		segments.push({ type: "text", text: source.slice(last) });
	}
	return segments;
}

function pickMatch(detected, hits) {
	const key = numberKey(detected.number);
	return (hits || []).find((hit) => hit.type === detected.entityType && hit.numberKey === key) || null;
}

export {
	ENTITY_PATTERNS,
	TYPE_LABELS,
	numberKey,
	typeLabel,
	formatMarker,
	detectEntityNumbers,
	entityNumberAtCaret,
	matchesSuggestion,
	parseModel,
	pickMatch,
};
